import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { CreateAllergyBody } from '@/types/patient-chart-api';

/**
 * POST /api/Patients/CreateAllergy
 *
 * Mirrors Flutter `DocumentDataSourceImpl.createAllergy`
 * (`patient_chart_data_source_impl.dart:314-331`). Wire body curl-verified
 * by Andrew 2026-05-19.
 *
 * Body shape: see `CreateAllergyBody` type doc — every field is
 * curl-locked verbatim, including the literal `dataAbsentReason: "n/a"`,
 * `reaction.onset: "n/a"`, `reaction.severity: "n/a"`, and the redundant
 * top-level `category: null` (the real category Coding lives nested
 * under `code.category`).
 *
 * Date format reminder: `onset` ships as `YYYY-MM-DDTHH:mm:ss.sss`
 * without the trailing `Z`. The .NET model binder rejects the UTC
 * marker — apply `stripUtcZ()` from `@/shared/lib/date-format` (or
 * inline `.replace(/Z$/, '')`) before posting.
 *
 * Response: plain string (the new allergy GUID).
 *
 * Cache: invalidates `['patient-allergies', patientId]` so the chart
 * Allergies tab refetches automatically after save. The SignalR
 * `UpdatePatientChat` "Allergy" event will ALSO fire and trigger the
 * same invalidation for concurrent sessions (PROD-827 Slice 3).
 */

interface CreateAllergyParams {
  patientId: string;
  body: CreateAllergyBody;
}

export function useCreateAllergy() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateAllergyParams>({
    mutationFn: ({ body }) =>
      post<string>('/api/Patients/CreateAllergy', body),
    onSuccess: (_data, { patientId }) => {
      // Dedup with the SignalR echo of this same mutation —
      // see `invalidateIfNotInFlight` doc.
      invalidateIfNotInFlight(qc, ['patient-allergies', patientId]);
    },
  });
}
